import React from 'react';

import type { ApiClient } from '../api/client';
import { BackgroundJobButton, type JobState } from './BackgroundJobButton';
import type { AppItem, AutopilotMode, HookStatus } from '../types';

export function AppDetailAutomationSettings({
  apiClient,
  appId,
  app,
  selectedContainer,
  hasContainers,
  hookUrl,
  hookStatus,
  hookInstalled,
  hookDisabled,
  hookIncomplete,
  hookNeedsRecreate,
  hookBusy,
  autofixEnabled,
  autofixFeatureBranches,
  autofixPrompt,
  autofixResult,
  appAutopilotMode,
  appEnablePushReminder,
  appTemplateBuildFailed,
  appTemplateMergeConflict,
  appTemplateReadySemi,
  appTemplateReadyFull,
  appAutopilotResult,
  autoMrFeedback,
  mrFeedbackResult,
  restartResult,
  backgroundJobResult,
  onSetAutofixEnabled,
  onSetAutofixFeatureBranches,
  onSetAutofixPrompt,
  onSaveAutofix,
  onResetAutofixTemplate,
  onSetAppAutopilotMode,
  onSetAppEnablePushReminder,
  onSetAppTemplateBuildFailed,
  onSetAppTemplateMergeConflict,
  onSetAppTemplateReadySemi,
  onSetAppTemplateReadyFull,
  onSaveAppAutopilot,
  onSetAutoMrFeedback,
  onSaveMrFeedback,
  onAddGitlabHook,
  onRemoveGitlabHook,
  onSetSelectedContainer,
  onRestartContainers,
  onSetBackgroundJobResult,
  onSetError
}: {
  apiClient: ApiClient;
  appId: string;
  app: AppItem;
  selectedContainer: string;
  hasContainers: boolean;
  hookUrl: string | null;
  hookStatus: HookStatus | null;
  hookInstalled: boolean | null;
  hookDisabled: boolean;
  hookIncomplete: boolean;
  hookNeedsRecreate: boolean;
  hookBusy: boolean;
  autofixEnabled: boolean;
  autofixFeatureBranches: boolean;
  autofixPrompt: string;
  autofixResult: string | null;
  appAutopilotMode: AutopilotMode;
  appEnablePushReminder: boolean;
  appTemplateBuildFailed: string;
  appTemplateMergeConflict: string;
  appTemplateReadySemi: string;
  appTemplateReadyFull: string;
  appAutopilotResult: string | null;
  autoMrFeedback: boolean;
  mrFeedbackResult: string | null;
  restartResult: string | null;
  backgroundJobResult: { message: string; job?: JobState } | null;
  onSetAutofixEnabled: (value: boolean) => void;
  onSetAutofixFeatureBranches: (value: boolean) => void;
  onSetAutofixPrompt: (value: string) => void;
  onSaveAutofix: () => void | Promise<void>;
  onResetAutofixTemplate: () => void;
  onSetAppAutopilotMode: (value: AutopilotMode) => void;
  onSetAppEnablePushReminder: (value: boolean) => void;
  onSetAppTemplateBuildFailed: (value: string) => void;
  onSetAppTemplateMergeConflict: (value: string) => void;
  onSetAppTemplateReadySemi: (value: string) => void;
  onSetAppTemplateReadyFull: (value: string) => void;
  onSaveAppAutopilot: () => void | Promise<void>;
  onSetAutoMrFeedback: (value: boolean) => void;
  onSaveMrFeedback: () => void | Promise<void>;
  onAddGitlabHook: () => void | Promise<void>;
  onRemoveGitlabHook: () => void | Promise<void>;
  onSetSelectedContainer: (value: string) => void;
  onRestartContainers: (container?: string) => void | Promise<void>;
  onSetBackgroundJobResult: (value: { message: string; job?: JobState } | null) => void;
  onSetError: (message: string | null) => void;
}) {
  return (
    <>
      <div className="card">
        <div className="card-header">
          <h3>Autofix on build failure</h3>
        </div>
        <div className="stack">
          <p className="status">Requires a GitLab webhook to be installed.</p>
          <label className="row">
            <input
              type="checkbox"
              checked={autofixEnabled}
              onChange={(e) => onSetAutofixEnabled(e.target.checked)}
            />
            Enable autofix
          </label>
          <label className="row">
            <input
              type="checkbox"
              checked={autofixFeatureBranches}
              onChange={(e) => onSetAutofixFeatureBranches(e.target.checked)}
            />
            <span>Work on feature branches</span>
            <span className="hint">Creates a merge request with fixes</span>
          </label>
          <label className="stack">
            <span>Autofix prompt template</span>
            <textarea
              rows={10}
              value={autofixPrompt}
              onChange={(e) => onSetAutofixPrompt(e.target.value)}
            />
            <span className="status">
              Placeholders: {'{{app_name}}'}, {'{{project_path}}'}, {'{{ref}}'}, {'{{job_name}}'}
              , {'{{job_id}}'}, {'{{job_url}}'}, {'{{job_log}}'}
            </span>
          </label>
          <div className="row">
            <button onClick={() => void onSaveAutofix()}>Save autofix settings</button>
            <button type="button" onClick={onResetAutofixTemplate}>
              Reset default
            </button>
          </div>
          {autofixResult && <p className="status">{autofixResult}</p>}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Conversation Autopilot</h3>
        </div>
        <div className="stack">
          <p className="status">Default behavior for new conversations in this app.</p>
          <label className="stack">
            <span>Default autopilot mode</span>
            <select
              value={appAutopilotMode}
              onChange={(event) => onSetAppAutopilotMode(event.target.value as AutopilotMode)}
            >
              <option value="off">Off</option>
              <option value="semi">Semi</option>
              <option value="full">Full</option>
            </select>
          </label>
          <label className="row">
            <input
              type="checkbox"
              checked={appEnablePushReminder}
              onChange={(e) => onSetAppEnablePushReminder(e.target.checked)}
            />
            <span>Enable push reminder</span>
            <span className="hint">Remind to push changes after work is done (experimental)</span>
          </label>
          <p className="status">Template overrides (leave empty to use system defaults).</p>
          <label className="stack">
            <span>Build failed template</span>
            <textarea
              rows={3}
              value={appTemplateBuildFailed}
              onChange={(event) => onSetAppTemplateBuildFailed(event.target.value)}
            />
          </label>
          <label className="stack">
            <span>Merge conflict template</span>
            <textarea
              rows={3}
              value={appTemplateMergeConflict}
              onChange={(event) => onSetAppTemplateMergeConflict(event.target.value)}
            />
          </label>
          <label className="stack">
            <span>Ready (semi) template</span>
            <textarea
              rows={2}
              value={appTemplateReadySemi}
              onChange={(event) => onSetAppTemplateReadySemi(event.target.value)}
            />
          </label>
          <label className="stack">
            <span>Ready (full) template</span>
            <textarea
              rows={2}
              value={appTemplateReadyFull}
              onChange={(event) => onSetAppTemplateReadyFull(event.target.value)}
            />
          </label>
          <button onClick={() => void onSaveAppAutopilot()}>Save autopilot defaults</button>
          {appAutopilotResult && <p className="status">{appAutopilotResult}</p>}
          <p className="status">
            Placeholders: {'{{app_name}}'}, {'{{app_slug}}'}, {'{{source_branch}}'}, {'{{target_branch}}'}
            , {'{{mr_iid}}'}, {'{{mr_url}}'}, {'{{pipeline_id}}'}, {'{{pipeline_status}}'}, {'{{pipeline_url}}'}
            , {'{{conversation_id}}'}, {'{{conversation_url}}'}, {'{{autopilot_mode}}'}
          </p>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Merge Request Feedback</h3>
        </div>
        <div className="stack">
          <p className="status">
            Automatically post MR feedback to the conversation thread. Requires a GitLab webhook
            to be installed.
          </p>
          <label className="row">
            <input
              type="checkbox"
              checked={autoMrFeedback}
              onChange={(e) => onSetAutoMrFeedback(e.target.checked)}
            />
            <span>Enable automatic MR feedback</span>
            <span className="hint">Posts MR comments and discussions as messages to the conversation</span>
          </label>
          <button onClick={() => void onSaveMrFeedback()}>Save MR feedback settings</button>
          {mrFeedbackResult && <p className="status">{mrFeedbackResult}</p>}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>GitLab webhook</h3>
        </div>
        <div className="stack">
          <div className="row">
            <span>
              Webhook status:{' '}
              <span
                className={`status-dot ${
                  hookInstalled === null
                    ? 'status-dot--unknown'
                    : hookDisabled
                      ? 'status-dot--disabled'
                      : hookIncomplete
                        ? 'status-dot--incomplete'
                        : hookInstalled
                          ? 'status-dot--ok'
                          : 'status-dot--missing'
                }`}
                aria-label={
                  hookInstalled === null
                    ? 'Webhook status unknown'
                    : hookDisabled
                      ? 'Webhook disabled'
                      : hookIncomplete
                        ? 'Webhook incomplete'
                        : hookInstalled
                          ? 'Webhook installed'
                          : 'Webhook missing'
                }
              />
              {hookInstalled === null
                ? 'unknown'
                : hookDisabled
                  ? 'disabled'
                  : hookIncomplete
                    ? 'incomplete'
                    : hookInstalled
                      ? 'installed'
                      : 'missing'}
            </span>
            {hookInstalled !== true && (
              <button onClick={() => void onAddGitlabHook()} disabled={hookBusy}>
                {hookBusy ? 'Adding hook...' : 'Add hook'}
              </button>
            )}
            {hookNeedsRecreate && (
              <button onClick={() => void onAddGitlabHook()} disabled={hookBusy}>
                {hookBusy
                  ? hookDisabled
                    ? 'Fixing hook...'
                    : 'Recreating hook...'
                  : hookDisabled
                    ? 'Fix hook'
                    : 'Recreate hook'}
              </button>
            )}
            {hookInstalled && (
              <button
                onClick={() => void onRemoveGitlabHook()}
                disabled={hookBusy}
                className="secondary"
              >
                {hookBusy ? 'Removing...' : 'Remove hook'}
              </button>
            )}
            {hookUrl && (
              <a href={hookUrl} target="_blank" rel="noreferrer">
                Hooks
              </a>
            )}
          </div>
          {hookStatus?.warnings && hookStatus.warnings.length > 0 && (
            <div className="status">
              <strong>Warnings:</strong> {hookStatus.warnings.join(' · ')}
            </div>
          )}
          {hookStatus?.webhook_id && (
            <p className="status">Webhook #{hookStatus.webhook_id} registered.</p>
          )}
          {hookStatus?.last_event_at ? (
            <p className="status">
              Last hook event: {new Date(hookStatus.last_event_at).toLocaleString()}
              {hookStatus.last_event_status ? ` · ${hookStatus.last_event_status}` : ''}
              {hookStatus.last_event_job_id ? ` · job #${hookStatus.last_event_job_id}` : ''}
              {hookStatus.last_event_pipeline_id ? ` · pipeline #${hookStatus.last_event_pipeline_id}` : ''}
            </p>
          ) : (
            <p className="status">No hook events recorded yet.</p>
          )}
          {hookStatus?.last_deploy_trigger_at ? (
            <p className="status">
              Last deploy trigger: {new Date(hookStatus.last_deploy_trigger_at).toLocaleString()}
              {hookStatus.last_deploy_trigger_source ? ` · ${hookStatus.last_deploy_trigger_source}` : ''}
              {hookStatus.last_deploy_trigger_job_id ? ` · job #${hookStatus.last_deploy_trigger_job_id}` : ''}
              {hookStatus.last_deploy_trigger_pipeline_id
                ? ` · pipeline #${hookStatus.last_deploy_trigger_pipeline_id}`
                : ''}
            </p>
          ) : (
            <p className="status">No deploy trigger recorded yet.</p>
          )}
          <p className="status">Used for autofix and deploy-if-green.</p>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Restart containers</h3>
        </div>
        <div className="stack">
          <div className="row">
            <select
              value={selectedContainer}
              onChange={(e) => onSetSelectedContainer(e.target.value)}
              disabled={!hasContainers}
            >
              <option value="">Restart all</option>
              {app.container_names.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            <button
              onClick={() => void onRestartContainers(selectedContainer || undefined)}
              disabled={!hasContainers}
            >
              Restart
            </button>
          </div>
          {!hasContainers && <p>No containers configured.</p>}
          {restartResult && <div className="mono">{restartResult}</div>}
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3>Fix file permissions (admin)</h3>
        </div>
        <div className="stack">
          <p className="status">
            Runs a docker container to fix file permissions on the conversation server. This will
            chown -R /var/lib/projects to UID 10001.
          </p>
          <div className="row">
            <BackgroundJobButton
              apiClient={apiClient}
              appId={appId}
              action="chown-fix"
              buttonLabel="Fix permissions (UID 10001)"
              params={{ target_uid: 10001 }}
              onComplete={(job) => {
                onSetBackgroundJobResult({ message: 'Permissions fix completed.', job });
              }}
              onError={(error) => {
                onSetBackgroundJobResult({ message: 'Live job status disconnected.' });
                onSetError(`Background job failed: ${error}`);
              }}
              className="secondary"
            />
          </div>
          {backgroundJobResult && <div className="status">{backgroundJobResult.message}</div>}
        </div>
      </div>
    </>
  );
}
